// mui
import { Box, Container, Typography, Button } from '@mui/material';
import { format } from 'date-fns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
// component
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs/custom-breadcrumbs';
import { useSnackbar } from 'src/components/snackbar';

// routes
import { paths } from 'src/routes/paths';
import {
  useGetAdministrationStats,
  useGetCurrentMonthStats,
  useGetLogisticsStats,
  sendExciseReportEmail,
} from 'src/api/admin';

import { useLocales } from 'src/locales';
import LogisticsSection from './view/logistics-section';
import AdministrationSection from './view/administration-section';
import CurrentMonthStatsSection from './view/current-month-stats-section';

export default function DashboardView() {
  const { t } = useLocales('overviewPage');
  const { enqueueSnackbar } = useSnackbar();
  const now = new Date();
  const currentDate = format(now, 'yyyy-MM-dd');
  const [exciseReportDate, setExciseReportDate] = useState<Dayjs | null>(dayjs());

  const { data: logisticsData } = useGetLogisticsStats({ currentDate });
  const { data: administrationData } = useGetAdministrationStats();
  const { data: currentMonthData } = useGetCurrentMonthStats();

  const emailExciseReport = async () => {
    try {
      const dateToSend = exciseReportDate ? exciseReportDate.format('YYYY-MM-DD') : currentDate;
      await sendExciseReportEmail({ date: dateToSend });
      enqueueSnackbar('Excise report sent successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error sending excise report', error);
      const message = error?.response?.data?.message || error.message;
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  return (
    <Container maxWidth="xl">
      <CustomBreadcrumbs
        heading={t('dashboard')}
        links={[{ name: t('admin'), href: paths.admin.root }, { name: t('overview') }]}
        sx={{ mb: [3, 3, 5] }}
      />

      <Box
        display="flex"
        flexDirection="column"
        justifyContent={{ xs: 'center', sm: 'space-between' }}
        alignItems={{ xs: 'stretch', sm: 'start' }}
        gap={8}
      >
        <Box display="flex" flexDirection="column" width="100%" gap={3} sx={{ overflowX: 'auto' }}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="h4">{t('logistics')}</Typography>
            <Box display="flex" flexDirection="row" alignItems="center" gap={2}>
              <DatePicker
                label="Excise Report Date"
                value={exciseReportDate}
                onChange={(newValue) => setExciseReportDate(newValue)}
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
              <Button onClick={emailExciseReport} variant="contained">
                Email Excise Report
              </Button>
            </Box>
          </Box>
          {logisticsData && <LogisticsSection {...logisticsData} />}
        </Box>

        <Box display="flex" flexDirection="column" width="100%" gap={3} sx={{ overflowX: 'auto' }}>
          <Typography variant="h4">{t('administration')}</Typography>
          {administrationData && <AdministrationSection administrationData={administrationData} />}
        </Box>

        <Box display="flex" flexDirection="column" width="100%" gap={3} sx={{ overflowX: 'auto' }}>
          <Typography variant="h4">{t('current_month_statistics')}</Typography>
          {currentMonthData && <CurrentMonthStatsSection currentMonthData={currentMonthData} />}
        </Box>
      </Box>
    </Container>
  );
}
